/**
 * Canonical scale for colour-grading effect parameters.
 *
 * `temperature.value`, `tint.value` and `tonal.shadows` / `.midtones` /
 * `.highlights` are authored, persisted and edited on -100..100. That is the
 * range the inspector sliders expose, the range the effect browser's presets
 * carry, and the range already stored in saved projects, so it is the meaning
 * a stored value has. Changing this constant reinterprets data on disk.
 *
 * Grading maths runs in 0..1 colour space and the grading shader uniforms are
 * declared -1..1, so each consumer converts through normalizeGradeParam rather
 * than assuming a scale of its own.
 */
export const GRADE_PARAM_SCALE = 100;

/** Convert an authored -100..100 grading parameter to its -1..1 amount. */
export function normalizeGradeParam(value: number): number {
  return value / GRADE_PARAM_SCALE;
}

/**
 * Canonical scale for video filter strength parameters.
 *
 * `brightness.value` and the `amount` of `sharpen`, `vignette` and `grain` are
 * authored, persisted and edited as percentages: brightness on -100..100,
 * sharpen on 0..200, vignette and grain on 0..100. That is what the inspector
 * sliders expose, what the effect browser's presets and the filter presets
 * carry, and what is already stored in saved projects, so it is the meaning a
 * stored value has. Changing this constant reinterprets data on disk.
 *
 * Filter maths runs in 0..1 colour space in both the CPU and shader paths, so
 * each consumer converts through normalizeFilterStrength rather than assuming a
 * scale of its own. Shape params (vignette midpoint, roundness and feather,
 * grain roughness) and the contrast and saturation multipliers are not on this
 * scale and pass through untouched.
 */
export const FILTER_STRENGTH_SCALE = 100;

/** Convert an authored percentage filter strength to its 0..1 amount. */
export function normalizeFilterStrength(value: number): number {
  return value / FILTER_STRENGTH_SCALE;
}
